/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import _ from 'underscore' // Npm: underscore library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io'
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import VendorAccounttDirectoryUpsert from 'components/VendorAccounttDirectoryUpsert'
import VendorAccounttDirectoryDropDownOptions from 'components/VendorAccounttDirectoryDropDownOptions'
import VendorAccounttDirectoryDelete from 'components/VendorAccounttDirectoryDelete'


/*
 * STYLES
 */
import {
  buttonStyle
} from './index.style'


/*
 * GRAPHS
 */
import VendorAccounttDirectoryReadQuery from './__query__/index.vendorAccounttDirectory.read.query'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 10,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [vendorAccounttDirectoryToSearch, setVendorAccounttDirectoryToSearch] = React.useState('')
  const { 'isOpen': isVendorAccounttDirectoryCreateOpen, 'onOpen': onVendorAccounttDirectoryCreateOpen, 'onClose': onVendorAccounttDirectoryCreateClose } = useDisclosure()
  const { 'isOpen': isVendorAccounttDirectoryUpdateOpen, 'onOpen': onVendorAccounttDirectoryUpdateOpen, 'onClose': onVendorAccounttDirectoryUpdateClose } = useDisclosure()
  const { 'isOpen': isVendorAccounttDirectoryDeleteOpen, 'onOpen': onVendorAccounttDirectoryDeleteOpen, 'onClose': onVendorAccounttDirectoryDeleteClose } = useDisclosure()
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)
  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(j => {
      const nextPaginationModel = { ...j, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])
  const _QueryVendorAccounttDirectoryReadVariables = React.useMemo(() => ({
    vendorAccounttDirectoryToSearch,
    'skip': paginationModel?.pageSize * paginationModel?.page,
    'take': paginationModel?.pageSize
  }), [paginationModel?.page, paginationModel?.pageSize, vendorAccounttDirectoryToSearch])
  const _QueryVendorAccounttDirectoryRead = useQuery(VendorAccounttDirectoryReadQuery, {
    'variables': _QueryVendorAccounttDirectoryReadVariables,
    'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy
  })
  const _onSearchInputChange = React.useRef(_.debounce((value) => {
    _ResetPagination()
    setVendorAccounttDirectoryToSearch(value)
  }, 800)).current
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleFilterModelChange = React.useCallback(__filterModel => {
    _ResetPagination()
    setFilterModel(__filterModel)
  }, [_ResetPagination])
  const _HandleColumnVisibilityModelChange = React.useCallback(newModel => {
    setColumnVisibilityModel(newModel)
  }, [])
  const _QueryVendorAccounttDirectoryReadRowCount = _.pluck(
    _QueryVendorAccounttDirectoryRead?.data?.VendorAccounttDirectoryRead,
    '_totalCount'
  )?.[0]
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryVendorAccounttDirectoryReadRowCount && null !== _QueryVendorAccounttDirectoryReadRowCount) _rowCountRef.current = _QueryVendorAccounttDirectoryReadRowCount

    return _rowCountRef.current
  }, [_QueryVendorAccounttDirectoryReadRowCount])
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': false, 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true }) ?? false

  // Extract search from filter model
  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = displayNameFilter?.value || ''
    _onSearchInputChange(searchValue)
  }, [_onSearchInputChange, filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSearchInputChange.cancel?.()
    }
  }, [])

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)

    try {
      await _QueryVendorAccounttDirectoryRead.refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [_QueryVendorAccounttDirectoryRead.refetch])

  // Data assignment.
  const columns = React.useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 150,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        editable: false,
        sortable: false,
        filterable: false,
        headerAlign: "center",
        hide: true,
      },
      {
        field: "displayName",
        headerName: "Display Name",
        description: "Display name of vendor account directory.",
        editable: false,
        sortable: false,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 280,
      },
      {
        field: "totalVendorAccount",
        headerName: "Total VendorAccount",
        description: "Total vendor accounts.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 280,
      },
      {
        field: "createdAt",
        headerName: "Created At",
        description: "Created date.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 280,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        description: "Updated date.",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        disableColumnMenu: true,
        headerAlign: "center",
        width: 280,
      },
      {
        field: "action",
        flex: 1,
        headerName: "Actions",
        description: "Actions for vendor account directory.",
        editable: false,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 210,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <VendorAccounttDirectoryDropDownOptions
            vendorAccounttDirectoryId={row?.id}
            onVendorAccounttDirectoryUpdate={
              onVendorAccounttDirectoryUpdateOpen
            }
            onVendorAccounttDirectoryDelete={
              onVendorAccounttDirectoryDeleteOpen
            }
          />
        ),
      },
    ],
    [onVendorAccounttDirectoryUpdateOpen, onVendorAccounttDirectoryDeleteOpen],
  );

  const rows = React.useMemo(() => _.compact(_QueryVendorAccounttDirectoryRead?.data?.VendorAccounttDirectoryRead?.map((r, index) => _successFlags.includes(r.status) ? ({
    'id': r?.id ?? `vendoraccountt-directory-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'displayName': r?.displayName ?? '-',
    'totalVendorAccount': r?.VendorAccountt?.length ?? '-',
    'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD hh:mm:ss A'),
    'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD hh:mm:ss A')
  }) : null)), [_QueryVendorAccounttDirectoryRead?.data?.VendorAccounttDirectoryRead, _successFlags, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className="vendorAccounttDirectory base">
        <Flex
          display="flex"
          flex="0 0 auto"
          flexDirection="column"
          position="relative"
          gap={_isCurrentViewMobile ? "12px" : "22px"}
          bg="white"
          minH="calc(100vh - 140px)"
          height="auto"
          overflow="visible"
          borderRadius="20px"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          <Flex pb="0px" justify="space-between" gap="12px" flexWrap="wrap">
            <Flex gap="12px">
              <Tooltip label="Create New Folder" fontSize="sm">
                <Button
                  w="170px"
                  leftIcon={<IoMdAdd />}
                  onClick={onVendorAccounttDirectoryCreateOpen}
                  style={buttonStyle}
                >
                  Create New
                </Button>
              </Tooltip>
            </Flex>
            <Flex gap="12px">
              <Button
                borderRadius="8px"
                fontWeight="450"
                minW="20px"
                backgroundColor="purple.200"
                color="purple"
                _hover={{ bg: "purple.800", color: "white" }}
                leftIcon={<HiArrowPath />}
                isDisabled={isRefreshing}
                onClick={_HandleRefresh}
              >
                Refresh
              </Button>
              <DownloadToExcel
                cellsData={
                  0 ===
                    _QueryVendorAccounttDirectoryRead?.data
                      ?.VendorAccounttDirectoryRead?.length ||
                  !_.every(
                    _.pluck(
                      _QueryVendorAccounttDirectoryRead?.data
                        ?.VendorAccounttDirectoryRead,
                      "status",
                    ),
                    (i) => _successFlags.includes(i),
                  )
                    ? []
                    : _QueryVendorAccounttDirectoryRead?.data?.VendorAccounttDirectoryRead.map(
                        (item, __index) => ({
                          "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                          "Display Name": item.displayName ?? "-",
                          "Total VendorAccount":
                            item?.VendorAccountt?.length ?? "-",
                          CreatedAt: Moment(item?.createdAt).format(
                            "YYYY-MM-DD",
                          ),
                          UpdatedAt: Moment(item?.updatedAt).format(
                            "YYYY-MM-DD",
                          ),
                        }),
                      )
                }
                headersData={[
                  "S.No.",
                  "Display Name",
                  "Total VendorAccount",
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>
          <DataGrid
            rows={rows}
            columns={columns}
            rowHeight={48}
            rowCount={rowCount}
            paginationMode="server"
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={_HandleFilterModelChange}
            paginationModel={paginationModel}
            onPaginationModelChange={_HandlePaginationModelChange}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              loadingOverlay: () => (
                <TableSpinner
                  isLoading={true}
                  isEmpty={false}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noResultsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
              noRowsOverlay: () => (
                <TableSpinner
                  isLoading={false}
                  isEmpty={true}
                  chopHeightFromHundredPercentage={
                    _tableHeaderHeightRef?.current?.clientHeight
                  }
                />
              ),
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={_HandleColumnVisibilityModelChange}
            loading={_QueryVendorAccounttDirectoryRead?.loading}
          />
        </Flex>
        <Modal
          size="md"
          title="VendorAccount Directory Create"
          isOpen={isVendorAccounttDirectoryCreateOpen}
          onClose={onVendorAccounttDirectoryCreateClose}
        >
          <VendorAccounttDirectoryUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size="md"
          title="VendorAccount Directory Update"
          isOpen={isVendorAccounttDirectoryUpdateOpen}
          onClose={onVendorAccounttDirectoryUpdateClose}
        >
          <VendorAccounttDirectoryUpsert />
        </Modal>
        <Modal
          size="md"
          title="VendorAccount Directory Delete"
          isOpen={isVendorAccounttDirectoryDeleteOpen}
          onClose={onVendorAccounttDirectoryDeleteClose}
        >
          <VendorAccounttDirectoryDelete />
        </Modal>
      </Flex>
    </>
  );
}


/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default Index
